import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

interface KnowledgeEntry {
  id: string;
  question: string;
  answer: string;
  source: string;
  category: string;
  created_at: string;
  superseded?: boolean;
  superseded_at?: string;
}

// Rodzaj wpisu (zrodlo). 'rule' = regula z poprawek operatora przy "Wyślij i ucz".
const SOURCES: Record<string, string> = {
  '': 'Wszystkie rodzaje',
  'rule': 'Reguły (z poprawek)',
  'agent_approved': 'Odpowiedzi (Wyślij i ucz)',
  'manual': 'Dodane ręcznie',
  'file': 'Z pliku',
  'crawler_www': 'Strona WWW',
};
const SOURCE_LABEL: Record<string, string> = {
  'rule': '📐 Reguła', 'agent_approved': 'Odpowiedź (Wyślij i ucz)', 'manual': 'Ręcznie', 'file': 'Plik', 'crawler_www': 'Strona WWW',
};

interface Props {
  API: string;
}

const CATEGORIES: Record<string, string> = {
  '': 'Wszystkie',
  'strona_www': 'Strona WWW',
  'faq': 'FAQ',
  'produkt': 'Produkt',
  'dostawa': 'Dostawa',
  'reklamacja': 'Reklamacja',
  'platnosc': 'Platnosc',
  'inne': 'Inne',
};

export default function KnowledgeManager({ API }: Props) {
  const [items, setItems] = useState<KnowledgeEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [category, setCategory] = useState('');
  const [source, setSource] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editQ, setEditQ] = useState('');
  const [editA, setEditA] = useState('');
  const [editCat, setEditCat] = useState('');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [addMode, setAddMode] = useState(false);
  const [newQ, setNewQ] = useState('');
  const [newA, setNewA] = useState('');
  const [newCat, setNewCat] = useState('faq');
  const [splitProposal, setSplitProposal] = useState<{question:string, answer:string}[] | null>(null);
  const [analyzingSplit, setAnalyzingSplit] = useState(false);
  const LIMIT = 25;

  const load = useCallback(async (s = search, cat = category, p = page, src = source) => {
    setLoading(true);
    try {
      const res = await axios.get(API + '/settings/knowledge/all', {
        params: { search: s, category: cat, skip: p * LIMIT, limit: LIMIT, source: src }
      });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch (e) {
      setStatus('Blad ladowania');
    }
    setLoading(false);
  }, [API, search, category, page, source]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (k: KnowledgeEntry) => {
    setEditingId(k.id);
    setEditQ(k.question);
    setEditA(k.answer);
    setEditCat(k.category || '');
  };

  const saveEdit = async (id: string) => {
    setSaving(true);
    try {
      await axios.put(API + '/settings/knowledge/' + id, { question: editQ, answer: editA, category: editCat });
      setEditingId(null);
      setStatus('Zapisano');
      setTimeout(() => setStatus(''), 2000);
      load();
    } catch (e) {
      setStatus('Blad zapisu');
    }
    setSaving(false);
  };

  const restoreOne = async (id: string) => {
    try {
      await axios.post(API + '/kb-rules/restore/' + id);
      setStatus('Przywrócono wpis');
      setTimeout(() => setStatus(''), 2000);
      load();
    } catch (e) { setStatus('Błąd przywracania'); }
  };

  const deleteOne = async (id: string) => {
    if (!window.confirm('Usunac ten wpis?')) return;
    await axios.delete(API + '/settings/knowledge/' + id);
    setStatus('Usunieto');
    setTimeout(() => setStatus(''), 2000);
    load();
  };

  const deleteBulk = async () => {
    if (!window.confirm('Usunac ' + selected.size + ' wpisow?')) return;
    await axios.delete(API + '/settings/knowledge/bulk', { data: Array.from(selected) });
    setSelected(new Set());
    setStatus('Usunieto ' + selected.size + ' wpisow');
    setTimeout(() => setStatus(''), 3000);
    load();
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const selectAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map(i => i.id)));
  };

  const exportData = (format: string) => {
    window.open(API + '/settings/knowledge/export?format=' + format, '_blank');
  };

  const addEntry = async () => {
    if (!newQ.trim() || !newA.trim()) { setStatus('Wypelnij pytanie i odpowiedz'); return; }
    setAnalyzingSplit(true);
    try {
      const res = await axios.post(API + '/settings/knowledge/analyze-split', { question: newQ, answer: newA });
      if (res.data.needs_split && res.data.entries.length > 1) {
        setSplitProposal(res.data.entries);
        setAnalyzingSplit(false);
        return;
      }
      const e = res.data.entries[0];
      await axios.post(API + '/settings/knowledge/add', { question: e.question, answer: e.answer, category: newCat });
      setNewQ(''); setNewA(''); setAddMode(false);
      setStatus('Dodano wpis');
      setTimeout(() => setStatus(''), 2000);
      load();
    } catch(e) { setStatus('Blad dodawania'); }
    setAnalyzingSplit(false);
  };
  const confirmSplitAndSave = async () => {
    if (!splitProposal) return;
    try {
      for (const e of splitProposal) {
        await axios.post(API + '/settings/knowledge/add', { question: e.question, answer: e.answer, category: newCat });
      }
      setStatus(`Dodano ${splitProposal.length} wpisow`);
      setSplitProposal(null);
      setNewQ(''); setNewA(''); setAddMode(false);
      setTimeout(() => setStatus(''), 2000);
      load();
    } catch(e) { setStatus('Blad zapisywania podzialu'); }
  };
  const saveWithoutSplit = async () => {
    if (!newQ.trim() || !newA.trim()) return;
    try {
      await axios.post(API + '/settings/knowledge/add', { question: newQ, answer: newA, category: newCat });
      setStatus('Dodano wpis bez podzialu');
      setSplitProposal(null);
      setNewQ(''); setNewA(''); setAddMode(false);
      setTimeout(() => setStatus(''), 2000);
      load();
    } catch(e) { setStatus('Blad dodawania'); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  const s: any = {
    card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 12 },
    input: { border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' as const, outline: 'none' },
    btn: { border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 },
    label: { fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: 0.5, display: 'block', marginBottom: 4 },
  };

  return (
    <div style={s.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          Baza wiedzy
          <span style={{ marginLeft: 8, background: '#f3f4f6', borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 400, color: '#6b7280' }}>
            {total} wpisow
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => exportData('csv')} style={{ ...s.btn, background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' }}>&#8595; CSV</button>
          <button onClick={() => exportData('json')} style={{ ...s.btn, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>&#8595; JSON</button>
          <button onClick={() => setAddMode(!addMode)} style={{ ...s.btn, background: addMode ? '#fee2e2' : '#1d4ed8', color: '#fff' }}>
            {addMode ? 'Anuluj' : '+ Dodaj wpis'}
          </button>
        </div>
      </div>

      {addMode && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10, color: '#1d4ed8' }}>Nowy wpis</div>
          <label style={s.label}>Pytanie / temat</label>
          <input value={newQ} onChange={e => setNewQ(e.target.value)} style={{ ...s.input, marginBottom: 8 }} placeholder="np. Jak dlugo trwa wysylka?" />
          <label style={s.label}>Odpowiedz</label>
          <textarea value={newA} onChange={e => setNewA(e.target.value)}
            style={{ ...s.input, minHeight: 80, resize: 'vertical', marginBottom: 8 }} placeholder="Tresc odpowiedzi..." />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select value={newCat} onChange={e => setNewCat(e.target.value)} style={{ ...s.input, width: 'auto' }}>
              {Object.entries(CATEGORIES).filter(([k]) => k !== '').map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <button onClick={addEntry} disabled={analyzingSplit} style={{ ...s.btn, background: '#1d4ed8', color: '#fff', opacity: analyzingSplit?0.6:1 }}>{analyzingSplit ? '⏳ Analizuję...' : 'Zapisz'}</button>
          </div>
        </div>
      )}
      {splitProposal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:'#fff',borderRadius:12,padding:24,width:700,maxWidth:'95vw',maxHeight:'85vh',overflowY:'auto'}}>
            <div style={{fontWeight:700,fontSize:16,marginBottom:8}}>📚 Proponowany podział na {splitProposal.length} wpisów</div>
            <div style={{fontSize:13,color:'#6b7280',marginBottom:16}}>
              Ta odpowiedź porusza kilka różnych tematów. Możesz edytować poszczególne wpisy, usunąć niepotrzebne, lub zapisać oryginalny tekst bez dzielenia.
            </div>
            {splitProposal.map((entry,i)=>(
              <div key={i} style={{border:'1px solid #e5e7eb',borderRadius:8,padding:12,marginBottom:12}}>
                <input value={entry.question} onChange={e=>{
                  const updated=[...splitProposal]; updated[i]={...updated[i],question:e.target.value}; setSplitProposal(updated);
                }} style={{width:'100%',padding:'6px 10px',border:'1px solid #d1d5db',borderRadius:6,fontSize:13,fontWeight:600,marginBottom:8,boxSizing:'border-box'}} />
                <textarea value={entry.answer} onChange={e=>{
                  const updated=[...splitProposal]; updated[i]={...updated[i],answer:e.target.value}; setSplitProposal(updated);
                }} rows={4} style={{width:'100%',padding:'6px 10px',border:'1px solid #d1d5db',borderRadius:6,fontSize:12,boxSizing:'border-box',resize:'vertical'}} />
                <button onClick={()=>{setSplitProposal(splitProposal.filter((_,idx)=>idx!==i));}}
                  style={{marginTop:6,padding:'4px 10px',background:'#fee2e2',color:'#dc2626',border:'1px solid #fca5a5',borderRadius:6,fontSize:11,cursor:'pointer'}}>
                  Usuń ten wpis
                </button>
              </div>
            ))}
            <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:16,flexWrap:'wrap'}}>
              <button onClick={()=>setSplitProposal(null)}
                style={{padding:'8px 16px',border:'1px solid #d1d5db',background:'transparent',borderRadius:6,fontSize:13,cursor:'pointer'}}>
                Anuluj
              </button>
              <button onClick={saveWithoutSplit}
                style={{padding:'8px 16px',border:'1px solid #d1d5db',background:'#f9fafb',borderRadius:6,fontSize:13,cursor:'pointer'}}>
                Zapisz bez dzielenia
              </button>
              <button onClick={confirmSplitAndSave}
                style={{padding:'8px 16px',background:'#1d4ed8',color:'#fff',border:'none',borderRadius:6,fontSize:13,cursor:'pointer',fontWeight:600}}>
                Zatwierdź i zapisz {splitProposal.length} wpisów
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10, lineHeight: 1.5 }}>
        📐 Reguły powstają automatycznie, gdy przy „Wyślij i ucz” poprawisz propozycję AI albo wpiszesz dla niej wskazówkę. Gdy nowa reguła przeczy starszej, starsza zostaje oznaczona jako zastąpiona i AI przestaje jej używać (można ją przywrócić). Przy sprzecznych wpisach AI stosuje nowszy.
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' as const }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { setSearch(searchInput); setPage(0); } }}
            placeholder="Szukaj... (Enter)" style={s.input} />
        </div>
        <select value={category} onChange={e => { setCategory(e.target.value); setPage(0); }}
          style={{ ...s.input, width: 'auto' }}>
          {Object.entries(CATEGORIES).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select value={source} onChange={e => { setSource(e.target.value); setPage(0); }}
          style={{ ...s.input, width: 'auto' }}>
          {Object.entries(SOURCES).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        {search && (
          <button onClick={() => { setSearch(''); setSearchInput(''); setPage(0); }}
            style={{ ...s.btn, background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' }}>x Wyczysc</button>
        )}
      </div>

      {selected.size > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, padding: '8px 12px' }}>
          <span style={{ fontSize: 12, color: '#92400e' }}>Zaznaczono: {selected.size}</span>
          <button onClick={deleteBulk} style={{ ...s.btn, background: '#dc2626', color: '#fff', fontSize: 11 }}>Usun zaznaczone</button>
          <button onClick={() => setSelected(new Set())} style={{ ...s.btn, background: '#f3f4f6', color: '#374151', fontSize: 11 }}>Odznacz</button>
        </div>
      )}

      {status && <div style={{ fontSize: 12, color: '#15803d', marginBottom: 8, fontWeight: 500 }}>{status}</div>}

      {loading ? (
        <div style={{ fontSize: 12, color: '#9ca3af', padding: 20, textAlign: 'center' as const }}>Ladowanie...</div>
      ) : items.length === 0 ? (
        <div style={{ fontSize: 12, color: '#9ca3af', padding: 20, textAlign: 'center' as const }}>Brak wynikow</div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '2px solid #e5e7eb', marginBottom: 4 }}>
            <input type="checkbox" checked={selected.size === items.length && items.length > 0} onChange={selectAll} style={{ cursor: 'pointer' }} />
            <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: 0.5 }}>
              Zaznacz wszystkie na stronie
            </span>
          </div>
          {items.map(k => (
            <div key={k.id} style={{ padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
              {editingId === k.id ? (
                <div style={{ background: '#f8fafc', border: '1px solid #3b82f6', borderRadius: 8, padding: 12 }}>
                  <label style={s.label}>Pytanie / temat</label>
                  <input value={editQ} onChange={e => setEditQ(e.target.value)} style={{ ...s.input, marginBottom: 8 }} />
                  <label style={s.label}>Odpowiedz</label>
                  <textarea value={editA} onChange={e => setEditA(e.target.value)}
                    style={{ ...s.input, minHeight: 100, resize: 'vertical', marginBottom: 8 }} />
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select value={editCat} onChange={e => setEditCat(e.target.value)} style={{ ...s.input, width: 'auto' }}>
                      {Object.entries(CATEGORIES).filter(([k]) => k !== '').map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                    <button onClick={() => saveEdit(k.id)} disabled={saving}
                      style={{ ...s.btn, background: '#1d4ed8', color: '#fff' }}>
                      {saving ? 'Zapis...' : 'Zapisz'}
                    </button>
                    <button onClick={() => setEditingId(null)}
                      style={{ ...s.btn, background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' }}>
                      Anuluj
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', opacity: k.superseded ? 0.55 : 1 }}>
                  <input type="checkbox" checked={selected.has(k.id)} onChange={() => toggleSelect(k.id)}
                    style={{ marginTop: 3, cursor: 'pointer', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#111827', marginBottom: 3, wordBreak: 'break-word' as const }}>
                      {k.question.length > 120 ? k.question.substring(0, 120) + '...' : k.question}
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4, wordBreak: 'break-word' as const }}>
                      {k.answer.length > 160 ? k.answer.substring(0, 160) + '...' : k.answer}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const }}>
                      <span style={{ fontSize: 10, background: '#f3f4f6', borderRadius: 4, padding: '1px 6px', color: '#6b7280' }}>{k.category || 'brak kategorii'}</span>
                      <span style={{ fontSize: 10, background: k.source === 'rule' ? '#f5f3ff' : '#eff6ff', borderRadius: 4, padding: '1px 6px', color: k.source === 'rule' ? '#6d28d9' : '#3b82f6', fontWeight: k.source === 'rule' ? 600 : 400 }}>{SOURCE_LABEL[k.source] || k.source || ''}</span>
                      <span style={{ fontSize: 10, color: '#9ca3af' }}>{(k.created_at || '').slice(0, 10)}</span>
                      {k.superseded ? <span style={{ fontSize: 10, background: '#fef3c7', borderRadius: 4, padding: '1px 6px', color: '#92400e' }}>zastąpiona {(k.superseded_at || '').slice(0, 10)} – AI jej nie używa</span> : null}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    {k.superseded ? (
                      <button onClick={() => restoreOne(k.id)}
                        style={{ ...s.btn, background: '#fffbeb', color: '#92400e', border: '1px solid #fcd34d', fontSize: 11, padding: '4px 10px' }}>
                        Przywróć
                      </button>
                    ) : null}
                    <button onClick={() => startEdit(k)}
                      style={{ ...s.btn, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontSize: 11, padding: '4px 10px' }}>
                      Edytuj
                    </button>
                    <button onClick={() => deleteOne(k.id)}
                      style={{ ...s.btn, background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', fontSize: 11, padding: '4px 10px' }}>
                      Usun
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 16, flexWrap: 'wrap' as const, alignItems: 'center' }}>
              <button onClick={() => setPage(0)} disabled={page === 0}
                style={{ ...s.btn, background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', opacity: page === 0 ? 0.4 : 1 }}>«</button>
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                style={{ ...s.btn, background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', opacity: page === 0 ? 0.4 : 1 }}>&#8249;</button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                const p = totalPages <= 7 ? i : Math.max(0, Math.min(page - 3, totalPages - 7)) + i;
                return (
                  <button key={p} onClick={() => setPage(p)}
                    style={{ ...s.btn, background: p === page ? '#1d4ed8' : '#f3f4f6', color: p === page ? '#fff' : '#374151', border: '1px solid #d1d5db', minWidth: 32 }}>
                    {p + 1}
                  </button>
                );
              })}
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                style={{ ...s.btn, background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', opacity: page >= totalPages - 1 ? 0.4 : 1 }}>&#8250;</button>
              <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}
                style={{ ...s.btn, background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', opacity: page >= totalPages - 1 ? 0.4 : 1 }}>»</button>
              <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 4 }}>
                Strona {page + 1} z {totalPages} ({total} wpisow)
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
